import { useState, useEffect, useCallback, useRef } from 'react';
import { reviewsAPI } from '../api/reviewsService';
import { getSortIdByLabel, getFilterIdByLabel, KBB_RESEARCHABLE_API } from '../constants';

/**
 * Custom hook for fetching and managing KBB reviews with smart caching optimization.
 * 
 * Prevents unnecessary API calls when filtering by specific star ratings and only the sort order changes,
 * since star-filtered results are already sorted by "Most Recent" and additional sorting has no effect.
 * 
 * @param {Object} params - Hook parameters
 * @param {Object} params.vehicleData - Vehicle information object containing vehicle details
 * @param {boolean} params.isOpen - Modal open state - triggers fetch when true
 * @param {number} params.currentPage - Current pagination page number (1-based)
 * @param {number} params.perPage - Number of reviews to display per page
 * @param {string} params.sortBy - Sort criteria label (e.g., "Most Recent", "Highest Rating", "Lowest Rating")
 * @param {string} params.filterBy - Filter criteria label (e.g., "All Ratings", "5 stars", "4 stars", etc.)
 * 
 * @returns {Object} Hook state and methods
 * @returns {Array} returns.paginatedReviews - Array of reviews for the current page
 * @returns {number} returns.totalPages - Total number of pages available
 * @returns {number} returns.totalReviews - Total count of all reviews matching the filter
 * @returns {Object} returns.skeletonLoading - Loading state object
 * @returns {boolean} returns.skeletonLoading.isLoadingReviews - Whether reviews are currently being fetched
 * @returns {boolean} returns.skeletonLoading.isLoadingTotalReviews - Whether total review count is being loaded
 * @returns {Function} returns.setTotalReviewsLoading - Manual setter for total reviews loading state
 * 
 * @example
 * const { paginatedReviews, totalPages, totalReviews, skeletonLoading } = useReviewsFetching({
 *   vehicleData: { make: 'Honda', model: 'Civic', year: 2024 },
 *   isOpen: true,
 *   currentPage: 1,
 *   perPage: 15,
 *   sortBy: 'Most Recent',
 *   filterBy: '5 stars'
 * });
 */

export const useReviewsFetching = ({ vehicleData, isOpen, currentPage, perPage, sortBy, filterBy }) => {
	const [paginatedReviews, setPaginatedReviews] = useState([]);
	const [totalPages, setTotalPages] = useState(0);
	const [totalReviews, setTotalReviews] = useState(0);
	const [skeletonLoading, setSkeletonLoading] = useState({
		isLoadingReviews: false,
		isLoadingTotalReviews: true,
	});
	
	// Track previous values of filterBy and sortBy
	const prevValuesRef = useRef({
		filterBy,
		sortBy
	});

	const fetchReviews = useCallback(async () => {
		// Determine what changed
		const filterChanged = filterBy !== prevValuesRef.current.filterBy;
		const sortChanged = sortBy !== prevValuesRef.current.sortBy;
		const isNotAllRatings = filterBy !== KBB_RESEARCHABLE_API.filter.ALL_RATINGS.label;
		
		// Prevent fetch when: filterBy is NOT "All Ratings" AND only sortBy changed
		const shouldPreventFetch = isNotAllRatings && sortChanged && !filterChanged;
		prevValuesRef.current = { filterBy, sortBy };
		
		if (shouldPreventFetch) {
			return;
		}
		
		setSkeletonLoading(s => ({ ...s, isLoadingReviews: true }));
		try {
			const sortValue = getSortIdByLabel(sortBy);
			const filterValue = getFilterIdByLabel(filterBy);

			// Service handles validation and throws errors on failure
			const data = await reviewsAPI.fetchReviews(vehicleData, currentPage, perPage, sortValue, filterValue);

			// Update state with fetched data
			setPaginatedReviews(data.results || []);
			setTotalPages(data.numPages || 0);
			setTotalReviews(data.totalReviews || 0);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('KBB Reviews - Failed to fetch:', error);
			setPaginatedReviews([]);
			setTotalPages(0);
			setTotalReviews(0);
		} finally {
			setSkeletonLoading(s => ({ ...s, isLoadingReviews: false, isLoadingTotalReviews: false }));
		}
	}, [vehicleData, currentPage, perPage, sortBy, filterBy]);

	// Fetch reviews when dependencies change
	useEffect(() => {
		if (!isOpen) return;
		fetchReviews();
	}, [isOpen, fetchReviews]);

	// Method to manually trigger total reviews loading skeleton
	const setTotalReviewsLoading = useCallback((isLoading) => {
		setSkeletonLoading(s => ({ ...s, isLoadingTotalReviews: isLoading }));
	}, []);

	return {
		paginatedReviews,
		totalPages,
		totalReviews,
		skeletonLoading,
		setTotalReviewsLoading,
	};
};
